import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DatabaseZapIcon, HardDriveDownloadIcon } from "lucide-react";
import type { IntegrationConfig } from "@/core/models/models";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { IntegrationCredential } from "@/core/db/schema/schema";
import { markdownComponents } from "./artifact-detail-sheet";
import useSWR from "swr";
import { useLocation, useNavigate } from "react-router-dom";

export const IntegrationDialog = ({
  intConfig,
  integrationsMutate,
  defaultOpen,
  open: controlledOpen,
  onOpenChange,
}: {
  intConfig: IntegrationConfig,
  integrationsMutate?: () => void,
  defaultOpen?: boolean,
  open?: boolean,
  onOpenChange?: (open: boolean) => void,
}) => {
  const { pathname } = useLocation();
  let navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };
  const [intInputs, setIntInputs] = useState<Record<string, string>>({});
  const [intOptions, setIntOptions] = useState<Record<string, string>>({});

  const { data: integrations } = useSWR<IntegrationCredential[]>(`integrations`, async (): Promise<IntegrationCredential[]> => {
    const res = await fetch(`${process.env.BUN_PUBLIC_BACKEND_BASE_URL}/api/integrations`, {
      method: "GET",
    });
    const body = await res.json();
    return body.integrations ?? [];
  });
  const existingIntegration = integrations?.find(
    (integration) => integration.integration.toLowerCase() === intConfig.integration.toLowerCase()
  );

  useEffect(() => {
    if (!existingIntegration) return;
    setIntInputs({
      apiKey: existingIntegration.apiKey ?? "",
      accessToken: existingIntegration.accessToken ?? "",
      refreshToken: existingIntegration.refreshToken ?? "",
      accessKey: existingIntegration.accessKey ?? "",
      secretKey: existingIntegration.secretKey ?? "",
      baseUrl: existingIntegration.baseUrl ?? "",
      tokenExpiration: existingIntegration.tokenExpiration ?? "",
    });
    setIntOptions(existingIntegration.options ?? {});
  }, [existingIntegration]);

  const inputsFilled = (intConfig.inputs ?? []).every(
    (input) => (intInputs[input.input]?.trim().length ?? 0) > 0
  );
  const optionsFilled = (intConfig.optionInputs ?? []).every(
    (option) => option.optional || (intOptions[option.key]?.trim().length ?? 0) > 0
  );
  const allFieldsFilled = inputsFilled && optionsFilled;

  const persistOauthOptions = async () => {
    if (!intConfig.optionInputs?.length) return;
    const integrationConfig: IntegrationCredential = {
      id: existingIntegration?.id ?? crypto.randomUUID(),
      integration: intConfig.integration,
      integrationType: intConfig.integrationType,
      apiKey: existingIntegration?.apiKey ?? null,
      accessToken: existingIntegration?.accessToken ?? null,
      refreshToken: existingIntegration?.refreshToken ?? null,
      accessKey: existingIntegration?.accessKey ?? null,
      secretKey: existingIntegration?.secretKey ?? null,
      baseUrl: existingIntegration?.baseUrl ?? null,
      tokenExpiration: existingIntegration?.tokenExpiration ?? null,
      options: intOptions,
    };
    await fetch(`${process.env.BUN_PUBLIC_BACKEND_BASE_URL}/api/integrations`, {
      method: "POST",
      body: JSON.stringify(integrationConfig),
      credentials: "include",
    });
  };

  const upsertIntegrationCreds = async (intConfig: IntegrationConfig) => {
    if (!allFieldsFilled) return;
    if (intConfig.integrationType === "BASIC_TOKEN") {
      const integrationConfig: IntegrationCredential = {
        id: crypto.randomUUID(),
        integration: intConfig.integration,
        integrationType: intConfig.integrationType,
        apiKey: intInputs["apiKey"] ?? null,
        accessToken: intInputs['accessToken'] ?? null,
        refreshToken: intInputs['refreshToken'] ?? null,
        accessKey: intInputs["accessKey"] ?? null,
        secretKey: intInputs["secretKey"] ?? null,
        baseUrl: intInputs["baseUrl"] ?? null,
        tokenExpiration: intInputs["tokenExpiration"] ?? null,
        options: intConfig.optionInputs?.length ? intOptions : null,
      }

      await fetch(`${process.env.BUN_PUBLIC_BACKEND_BASE_URL}/api/integrations`, {
        method: "POST",
        body: JSON.stringify(integrationConfig),
        credentials: "include",
      });
      if (integrationsMutate) integrationsMutate();
      toast("Credential Saved", { position: "top-center" })
      if (pathname.includes("config")) {
        navigate("/knowledge");
      } else {
        setOpen(false);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button variant={"default"} className="flex gap-1 items-center w-full">
            <DatabaseZapIcon size={16} />
            <div>Connect</div>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="font-sans">
        <DialogHeader>
          <DialogTitle className="text-lg">
            Connect your {intConfig.integration}
          </DialogTitle>
          <DialogDescription>
            {intConfig.description &&
              <ReactMarkdown components={markdownComponents}>{intConfig.description}</ReactMarkdown>
            }
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {intConfig.inputs?.map((input) => {
            return (
              <Input key={input.input + intConfig.integration} placeholder={input.label} value={intInputs[input.input] ?? ""} onChange={(e) => {
                e.preventDefault();
                setIntInputs((prev) => ({
                  ...prev, [input.input]: e.target.value
                }))
              }} />
            );
          })}
          {intConfig.optionInputs?.map((option) => {
            return (
              <Input key={option.key + intConfig.integration} placeholder={option.label} value={intOptions[option.key] ?? ""} onChange={(e) => {
                e.preventDefault();
                setIntOptions((prev) => ({
                  ...prev, [option.key]: e.target.value
                }))
              }} />
            );
          })}
        </div>
        <DialogFooter>
          <div className="w-full flex flex-col gap-2">
            {intConfig.installUrl &&
              <Button variant={"default"} className="flex items-center gap-2 w-full"
                onClick={async () => {
                  window.location.href = intConfig.installUrl!;
                }}>
                <HardDriveDownloadIcon size={16} />
                <div>Install</div>
              </Button>
            }
            <Button variant={"default"} className="flex items-center gap-2 w-full" disabled={(!allFieldsFilled && intConfig.integrationType !== "OAUTH")
              || (intConfig.integrationType === "OAUTH" && (!optionsFilled || !new URL(intConfig.oauthAuthorizationUrl!).searchParams.get("client_id")))}
              onClick={async () => {
                if (intConfig.integrationType !== "OAUTH") {
                  upsertIntegrationCreds(intConfig);
                  return;
                } else {
                  await persistOauthOptions();
                  window.location.href = intConfig.oauthAuthorizationUrl!;
                }
              }}>
              <DatabaseZapIcon size={16} />
              <div>Connect</div>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
